import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import JumpToSheet from './JumpToSheet';
import useActiveSection from './useActiveSection';
import styles from './ModernBottomNav.module.css';

// Brand accent color
const BRAND_ACCENT = '#B08D57';

// Inline SVG Icons
const DashboardIcon = ({ isActive }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M3 13H11V3H3V13ZM3 21H11V15H3V21ZM13 21H21V11H13V21ZM13 3V9H21V3H13Z"
      stroke={isActive ? BRAND_ACCENT : 'currentColor'}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const AssignmentsIcon = ({ isActive }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
      stroke={isActive ? BRAND_ACCENT : 'currentColor'}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M14 2V8H20"
      stroke={isActive ? BRAND_ACCENT : 'currentColor'}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M16 13H8"
      stroke={isActive ? BRAND_ACCENT : 'currentColor'}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M16 17H8"
      stroke={isActive ? BRAND_ACCENT : 'currentColor'}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10 9H9H8"
      stroke={isActive ? BRAND_ACCENT : 'currentColor'}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const QuizzesIcon = ({ isActive }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M9 11L12 14L22 4"
      stroke={isActive ? BRAND_ACCENT : 'currentColor'}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M21 12V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H16"
      stroke={isActive ? BRAND_ACCENT : 'currentColor'}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const JumpToIcon = ({ isActive }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 5V19M5 12H19"
      stroke={isActive ? BRAND_ACCENT : 'currentColor'}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle
      cx="12"
      cy="12"
      r="10"
      stroke={isActive ? BRAND_ACCENT : 'currentColor'}
      strokeWidth="2"
    />
  </svg>
);

const ModernBottomNav = ({
  sections = [],
  offset = 0,
  activeTab = null,
  onTabChange,
  onOpenJump,
  onSectionSelect
}) => {
  const navigate = useNavigate();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const activeSection = useActiveSection(sections, offset);

  const handleDashboardClick = useCallback(() => {
    navigate('/dashboard');
    if (onTabChange) {
      onTabChange('dashboard');
    }
  }, [navigate, onTabChange]);

  const handleAssignmentsClick = useCallback(() => {
    navigate('/assignments');
    if (onTabChange) {
      onTabChange('assignments');
    }
  }, [navigate, onTabChange]);

  const handleQuizzesClick = useCallback(() => {
    navigate('/student/quizzes');
    if (onTabChange) {
      onTabChange('quizzes');
    }
  }, [navigate, onTabChange]);

  const handleJumpClick = useCallback(() => {
    // If a custom onOpenJump handler is provided, use it (CourseDetail has its own sheet)
    // Otherwise, open the built-in sheet
    if (onOpenJump) {
      onOpenJump();
    } else {
      setIsSheetOpen(true);
    }
  }, [onOpenJump]);

  const handleSheetClose = useCallback(() => {
    setIsSheetOpen(false);
  }, []);

  const handleSectionSelect = useCallback((sectionId) => {
    // Call custom handler if provided
    if (onSectionSelect) {
      onSectionSelect(sectionId);
    }
    
    // Default scroll behavior
    const element = document.getElementById(sectionId);
    if (element) {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const elementTop = element.getBoundingClientRect().top + window.scrollY;
      const scrollPosition = elementTop - offset - 20;
      
      window.scrollTo({
        top: scrollPosition,
        behavior: prefersReducedMotion ? 'auto' : 'smooth'
      });

      const newHash = `#${sectionId}`;
      if (window.location.hash !== newHash) {
        window.history.replaceState(null, '', newHash);
      }
    }
    setIsSheetOpen(false);
  }, [offset, onSectionSelect]);

  return (
    <>
      <nav className={styles.navContainer} role="navigation" aria-label="Bottom navigation">
        <div className={styles.navTray}>
          <button
            className={styles.navItem}
            onClick={handleDashboardClick}
            aria-label="Dashboard"
            aria-current={activeTab === 'dashboard' ? 'page' : undefined}
          >
            <div className={`${styles.navIcon} ${activeTab === 'dashboard' ? styles.navIconActive : ''}`}>
              <DashboardIcon isActive={activeTab === 'dashboard'} />
            </div>
            <span className={`${styles.navLabel} ${activeTab === 'dashboard' ? styles.navLabelActive : ''}`}>
              Dashboard
            </span>
            {activeTab === 'dashboard' && (
              <div className={styles.activeIndicator} aria-hidden="true" />
            )}
          </button>

          <button
            className={styles.navItem}
            onClick={handleAssignmentsClick}
            aria-label="My Assignments"
            aria-current={activeTab === 'assignments' ? 'page' : undefined}
          >
            <div className={`${styles.navIcon} ${activeTab === 'assignments' ? styles.navIconActive : ''}`}>
              <AssignmentsIcon isActive={activeTab === 'assignments'} />
            </div>
            <span className={`${styles.navLabel} ${activeTab === 'assignments' ? styles.navLabelActive : ''}`}>
              Assignments
            </span>
            {activeTab === 'assignments' && (
              <div className={styles.activeIndicator} aria-hidden="true" />
            )}
          </button>

          <button
            className={styles.navItem}
            onClick={handleQuizzesClick}
            aria-label="My Quizzes"
            aria-current={activeTab === 'quizzes' ? 'page' : undefined}
          >
            <div className={`${styles.navIcon} ${activeTab === 'quizzes' ? styles.navIconActive : ''}`}>
              <QuizzesIcon isActive={activeTab === 'quizzes'} />
            </div>
            <span className={`${styles.navLabel} ${activeTab === 'quizzes' ? styles.navLabelActive : ''}`}>
              Quizzes
            </span>
            {activeTab === 'quizzes' && (
              <div className={styles.activeIndicator} aria-hidden="true" />
            )}
          </button>

          <button
            className={styles.navItem}
            onClick={handleJumpClick}
            aria-label="Jump to section"
            aria-expanded={isSheetOpen}
            aria-current={isSheetOpen ? 'true' : undefined}
          >
            <div className={`${styles.navIcon} ${isSheetOpen ? styles.navIconActive : ''}`}>
              <JumpToIcon isActive={isSheetOpen} />
            </div>
            <span className={`${styles.navLabel} ${isSheetOpen ? styles.navLabelActive : ''}`}>
              Jump To
            </span>
            {isSheetOpen && (
              <div className={styles.activeIndicator} aria-hidden="true" />
            )}
          </button>
        </div>
      </nav>

      {/* Only render built-in JumpToSheet if no custom onOpenJump handler is provided */}
      {!onOpenJump && (
        <JumpToSheet
          isOpen={isSheetOpen}
          title="Jump to"
          sections={sections}
          activeId={activeSection}
          onClose={handleSheetClose}
          onSelect={handleSectionSelect}
        />
      )}
    </>
  );
};

export default ModernBottomNav;
